/**
 * Human-friendly phone for display. Always render the result inside `<bdi dir="ltr">` so the
 * "+" and digit groups never flip in RTL text.
 * SA: +966 5X XXX XXXX, BH: +973 XXXX XXXX, otherwise "+CC" then the national digits grouped in threes.
 */
export function formatPhone(e164: string | null | undefined): string {
  if (!e164) return "";
  const digits = e164.replace(/\D/g, "");
  if (digits.length === 0) return "";
  if (digits.startsWith("966") && digits.length === 12) {
    const n = digits.slice(3);
    return `+966 ${n.slice(0, 2)} ${n.slice(2, 5)} ${n.slice(5)}`;
  }
  if (digits.startsWith("973") && digits.length === 11) {
    const n = digits.slice(3);
    return `+973 ${n.slice(0, 4)} ${n.slice(4)}`;
  }
  const cc = callingCodeOf(digits);
  const rest = digits.slice(cc.length);
  if (rest === "") return `+${cc}`;
  if (cc === "1" && rest.length === 10) {
    return `+1 ${rest.slice(0, 3)} ${rest.slice(3, 6)} ${rest.slice(6)}`;
  }
  return `+${cc} ${rest.replace(/(\d{3})(?=\d)/g, "$1 ")}`;
}

/** 2-digit calling codes (E.164 is prefix-free, so a longer match never conflicts). */
const CC2 = new Set(
  "20 27 30 31 32 33 34 36 39 40 41 43 44 45 46 47 48 49 51 52 53 54 55 56 57 58 60 61 62 63 64 65 66 81 82 84 86 90 91 92 93 94 95 98".split(
    " ",
  ),
);

/** Country calling code for a digits-only E.164 string: 1 digit (+1, +7), 2 digits, else 3. */
function callingCodeOf(digits: string): string {
  if (digits.startsWith("1") || digits.startsWith("7")) return digits.slice(0, 1);
  if (CC2.has(digits.slice(0, 2))) return digits.slice(0, 2);
  return digits.slice(0, 3);
}
